// src/pages/manager/attendance/ManagerDailyReport.jsx
import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  MenuItem,
} from "@mui/material";
import ManagerLayout from "../../layouts/ManagerLayout";
import api from "../../utils/api";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";

const ManagerDailyReport = () => {
  const admin = JSON.parse(localStorage.getItem("user"));
  const superAdminId = admin.superAdminId;

  const [from, setFrom] = useState(new Date().toISOString().slice(0, 10));
  const [to, setTo] = useState(new Date().toISOString().slice(0, 10));
  const [employees, setEmployees] = useState([]);
  const [selectedEmp, setSelectedEmp] = useState("all");
  const [rows, setRows] = useState([]);

  // load employees for filter dropdown
  useEffect(() => {
    api.get(`/api/employees/get-all/${superAdminId}`).then((res) => {
      setEmployees(res.data);
    });
  }, [superAdminId]);

  // fetch daily report
  const fetchReport = async () => {
    const res = await api.get(
      `/api/attendance/daily-report/${superAdminId}?from=${from}&to=${to}`
    );
    setRows(res.data);
  };

  // apply employee filter
  const filteredRows =
    selectedEmp === "all" ? rows : rows.filter((r) => r.empId === selectedEmp);

  // export to Excel
  const exportExcel = () => {
    const sheetData = filteredRows.map((r) => ({
      Date: r.date,
      Employee: r.employee,
      In: r.inTime,
      Out: r.outTime,
      "Lunch In": r.lunchIn,
      "Lunch Out": r.lunchOut,
      "Worked Hrs": r.workedHrs,
    }));
    const ws = XLSX.utils.json_to_sheet(sheetData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "DailyReport");
    XLSX.writeFile(wb, `daily_report_${from}_to_${to}.xlsx`);
  };

  // export to PDF
  const exportPDF = () => {
    const doc = new jsPDF();
    doc.text("Daily Attendance Report", 14, 16);
    doc.autoTable({
      startY: 20,
      head: [
        [
          "Date",
          "Employee",
          "In",
          "Out",
          "Lunch In",
          "Lunch Out",
          "Worked Hrs",
        ],
      ],
      body: filteredRows.map((r) => [
        r.date,
        r.employee,
        r.inTime,
        r.outTime,
        r.lunchIn,
        r.lunchOut,
        r.workedHrs,
      ]),
      theme: "grid",
    });
    doc.save(`daily_report_${from}_to_${to}.pdf`);
  };

  return (
    <ManagerLayout>
      <Container sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          Daily Attendance Report
        </Typography>

        <Box display="flex" gap={2} alignItems="center" mb={3}>
          <TextField
            label="From"
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="To"
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Employee"
            select
            value={selectedEmp}
            onChange={(e) => setSelectedEmp(e.target.value)}
          >
            <MenuItem value="all">All</MenuItem>
            {employees.map((emp) => (
              <MenuItem key={emp._id} value={emp._id}>
                {emp.name}
              </MenuItem>
            ))}
          </TextField>
          <Button variant="contained" onClick={fetchReport}>
            Generate
          </Button>
          <Button variant="outlined" onClick={exportExcel}>
            Export Excel
          </Button>
          <Button variant="outlined" onClick={exportPDF}>
            Export PDF
          </Button>
        </Box>

        <Paper>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Employee</TableCell>
                <TableCell>In Time</TableCell>
                <TableCell>Out Time</TableCell>
                <TableCell>Lunch In</TableCell>
                <TableCell>Lunch Out</TableCell>
                <TableCell>Worked Hrs</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRows.map((r, idx) => (
                <TableRow key={idx}>
                  <TableCell>{r.date}</TableCell>
                  <TableCell>{r.employee}</TableCell>
                  <TableCell>{r.inTime}</TableCell>
                  <TableCell>{r.outTime}</TableCell>
                  <TableCell>{r.lunchIn}</TableCell>
                  <TableCell>{r.lunchOut}</TableCell>
                  <TableCell>{r.workedHrs}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      </Container>
    </ManagerLayout>
  );
};

export default ManagerDailyReport;
